import React, { useState } from 'react';
import { Box, useTheme, useMediaQuery, IconButton, Tooltip, CircularProgress } from '@mui/material';
import { useExportAggregatorBreakdownMutation } from '@/services/dashboardApi';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import highcharts3d from 'highcharts/highcharts-3d';
import { ChartTable } from '../tables/ChartTable';
import CustomDatePicker from '../customDatePicker';
import {
  AggregatorTypeBreakdownResponse,
  useGetAggregatorTypeBreakdownQuery,
} from '@/services/dashboardApi';
import excelIcon from '../../assets/excel-icon.svg';
// Initialize 3D module
highcharts3d(Highcharts);

interface AggregatorPieChartProps {
  mode?: 'date' | 'gateway';
  onMenuItemSelect?: (item: string) => void;
  amcId?: string; // Optional AMC filter
}
const getAggregatorData = (data: AggregatorTypeBreakdownResponse, totalCount: number) => [
  {
    type: 'BILLDESK V1',
    transactionCount: data?.Billdesk1Count || 0,
    amount: data?.Billdesk1Amount?.toLocaleString('en-IN', {
      style: 'currency',
      currency: 'INR',
    }),
    percentage:
      totalCount === 0 ? '0%' : `${(((data?.Billdesk1Count || 0) * 100) / totalCount).toFixed(2)}%`,
  },
  {
    type: 'BILLDESK V2',
    transactionCount: data?.Billdesk2Count || 0,
    amount: data?.Billdesk2Amount?.toLocaleString('en-IN', {
      style: 'currency',
      currency: 'INR',
    }),
    percentage:
      totalCount === 0 ? '0%' : `${(((data?.Billdesk2Count || 0) * 100) / totalCount).toFixed(2)}%`,
  },
  {
    type: 'CAMS Pay',
    transactionCount: data?.CamsPayCount || 0,
    amount: data?.CamsPayAmount?.toLocaleString('en-IN', {
      style: 'currency',
      currency: 'INR',
    }),
    percentage:
      totalCount === 0 ? '0%' : `${(((data?.CamsPayCount || 0) * 100) / totalCount).toFixed(2)}%`,
  },
  {
    type: 'RAZORPAY',
    transactionCount: data?.RazorPayCount || 0,
    amount: data?.RazorPayAmount?.toLocaleString('en-IN', {
      style: 'currency',
      currency: 'INR',
    }),
    percentage:
      totalCount === 0 ? '0%' : `${(((data?.RazorPayCount || 0) * 100) / totalCount).toFixed(2)}%`,
  },
  {
    type: 'E PAY',
    transactionCount: data?.EPayCount || 0,
    amount: data?.EPayAmount?.toLocaleString('en-IN', {
      style: 'currency',
      currency: 'INR',
    }),
    percentage:
      totalCount === 0 ? '0%' : `${(((data?.EPayCount || 0) * 100) / totalCount).toFixed(2)}%`,
  },
];

export const AggregatorPieChart: React.FC<AggregatorPieChartProps> = ({
  onMenuItemSelect,
  amcId,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedDate, setSelectedDate] = React.useState<Date | null>(new Date());
  const [type, setType] = useState<'daily' | 'weekly' | 'monthly'>('daily');

  // Build API parameters with optional AMC filter
  const apiParams: any = {
    date: selectedDate?.toISOString() || new Date()?.toISOString(),
    type,
  };
  if (amcId) {
    apiParams.amcId = amcId;
  }

  const { data, isLoading } = useGetAggregatorTypeBreakdownQuery(apiParams);
  const [exportAggregatorBreakdown, { isLoading: isDownloading }] =
    useExportAggregatorBreakdownMutation();

  const handleDownload = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();

    try {
      const blob = await exportAggregatorBreakdown({
        amcId: amcId,
        date: selectedDate?.toISOString(),
        type: type,
      }).unwrap();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.style.display = 'none';
      link.href = url;
      link.download = 'aggregator-breakdown.xlsx';

      document.body.appendChild(link);

      // Use requestAnimationFrame to ensure proper download
      requestAnimationFrame(() => {
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      });
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download file. Please try again.');
    }
  };

  const totalCount =
    (data?.Billdesk1Count || 0) +
    (data?.Billdesk2Count || 0) +
    (data?.CamsPayCount || 0) +
    (data?.RazorPayCount || 0) +
    (data?.EPayCount || 0);

  const aggregatorChartOptions: Highcharts.Options = {
    credits: { enabled: false },
    chart: {
      type: 'pie',
      options3d: { enabled: true, alpha: 45, beta: 0 },
      height: '300px',
    },
    title: {
      text: '',
      align: 'left',
    },
    legend: {
      enabled: true,
      align: 'right',
      verticalAlign: 'middle',
      layout: 'vertical',
      itemStyle: {
        fontSize: '12px',
      },
    },
    plotOptions: {
      pie: {
        innerSize: '30%',
        allowPointSelect: true,
        cursor: 'pointer',
        depth: 40,
        dataLabels: {
          enabled: true,
          alignTo: 'connectors',
          connectorShape: 'straight',
          distance: 20,
          formatter: function () {
            return `${this.point.name} ${this.point?.y?.toFixed(2)}%`;
          },
          color: theme.palette.text.secondary,
        },
        showInLegend: true,
      },
    },
    series: [
      {
        type: 'pie',
        name: 'Share',
        animation: false,
        data: [
          {
            name: 'BILLDESK V1',
            y: isLoading
              ? 0
              : totalCount === 0
                ? 0
                : ((data?.Billdesk1Count || 0) * 100) / totalCount,
            color: theme.palette.piechart.debitCard,
          },
          {
            name: 'BILLDESK V2',
            y: isLoading
              ? 0
              : totalCount === 0
                ? 0
                : ((data?.Billdesk2Count || 0) * 100) / totalCount,
            color: theme.palette.piechart.upi,
          },
          {
            name: 'CAMS Pay',
            y: isLoading
              ? 0
              : totalCount === 0
                ? 0
                : ((data?.CamsPayCount || 0) * 100) / totalCount,
            color: theme.palette.piechart.enachmandates,
          },
          {
            name: 'RAZORPAY',
            y: isLoading
              ? 0
              : totalCount === 0
                ? 0
                : ((data?.RazorPayCount || 0) * 100) / totalCount,
            color: theme.palette.piechart.netBanking,
          },
          {
            name: 'E PAY',
            y: isLoading ? 0 : totalCount === 0 ? 0 : ((data?.EPayCount || 0) * 100) / totalCount,
            color: theme.palette.piechart.upimandates,
          },
        ],
      },
    ],
  };

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: '10px',
        position: 'relative',
        width: '100%',
      }}
    >
      {/* Custom Date Picker - Adjusted for Mobile */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 1.5,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 2 : 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              ...theme.typography.h3,
              fontWeight: theme.typography.h3.fontWeight,
              p: 2.75,
            }}
          >
            Aggregator Type Breakdown
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title="Download Excel">
              <IconButton
                onClick={handleDownload}
                disabled={isDownloading || totalCount === 0}
                size="small"
                sx={{
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  },
                  '&.Mui-disabled': {
                    color: theme.palette.action.disabled,
                  },
                }}
                aria-label="Export to Excel"
              >
                {isDownloading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  <img src={excelIcon} alt="Export to Excel" style={{ width: 20, height: 20 }} />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <CustomDatePicker
          disableFuture
          value={selectedDate}
          viewType={type === 'daily' ? 'day' : type === 'weekly' ? 'week' : 'month'}
          onChangeViewType={(newType) => {
            setType(newType === 'day' ? 'daily' : newType === 'week' ? 'weekly' : 'monthly');
            onMenuItemSelect?.(newType);
          }}
          onChange={(newValue) => {
            setSelectedDate(newValue);
            onMenuItemSelect?.(newValue?.toISOString() || '');
          }}
        />
      </Box>

      {/* Chart with Responsive Width */}
      <Box
        sx={{ width: '100%', maxWidth: '600px', height: isMobile ? '300px' : '400px', mx: 'auto' }}
      >
        <HighchartsReact highcharts={Highcharts} options={aggregatorChartOptions} />
      </Box>

      {/* Data Table */}
      {!isLoading && <ChartTable data={getAggregatorData(data!, totalCount)} title="Aggregator" />}
    </Box>
  );
};
